import { useMemo, useCallback, useRef, useEffect } from 'react';
import { ResponsiveLine } from '@nivo/line';
import { ProcessedHeartRateData } from '@repo/shared/dtos/devices/HeartRateDto';
import { SessionPpgData } from '@repo/shared/dtos/devices/SessionPpgDto';
import { SessionByDateDto } from '@repo/shared/dtos/my-insights/SessionByDateDto';
import { getSessionColor, filterDataByTimeRange, calculateYRange } from './utils';
import ExportButton from '../../../../utils/export/ExportButton';
import { ExportFormat } from '../../../../utils/export/types';
import { ZoomBrush } from './ZoomBrush';
import { chartThemeDark } from '@repo/ui/theme';

export interface OuraHeartRateChartProps {
  heartRateData: {
    data: ProcessedHeartRateData[];
    min: number;
    max: number;
  };
  sessionData?: SessionPpgData | null;
  allSessions?: SessionByDateDto[];
  showSession: boolean;
  zoomDomain: { xStart: Date; xEnd: Date } | null;
  colors: {
    primaryColor: string;
    sessionColor: string;
    markersColor: string;
    gridColor: string;
    textColor: string;
    titleColor: string;
  };
  onMarkerClick?: (sessionId: string) => void;
  onZoomChange?: (zoomDomain: { xStart: Date; xEnd: Date } | null) => void;
}

export const OuraHeartRateChart = ({
  heartRateData,
  sessionData,
  allSessions = [],
  showSession,
  zoomDomain,
  colors,
  onMarkerClick,
  onZoomChange,
}: OuraHeartRateChartProps) => {
  const previousZoomDomain = useRef(zoomDomain);
  const isZooming = useRef(false);

  const isBrushDisabled = sessionData && showSession;

  useEffect(() => {
    const currentZoom = zoomDomain;
    const prevZoom = previousZoomDomain.current;

    const zoomChanged = JSON.stringify(currentZoom) !== JSON.stringify(prevZoom);

    if (zoomChanged) {
      isZooming.current = true;

      previousZoomDomain.current = currentZoom;
    }

    return () => {
      isZooming.current = false;
    };
  }, [zoomDomain]);

  const chartDataKey = useMemo(() => {
    if (!heartRateData?.data?.length) return 'empty-chart';

    const sortedData = [...heartRateData.data].sort(
      (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
    );

    const firstDate = new Date(sortedData[0].timestamp);
    const lastDate = new Date(sortedData[sortedData.length - 1].timestamp);

    const firstDay = firstDate.toISOString().split('T')[0];
    const lastDay = lastDate.toISOString().split('T')[0];

    return `chart-${firstDay}-${lastDay}-${heartRateData.data.length}`;
  }, [heartRateData?.data]);

  const transformedData = useMemo(() => {
    const result = [];

    if (heartRateData?.data?.length > 0) {
      result.push({
        id: 'heartRate',
        data: heartRateData.data.map((point: ProcessedHeartRateData) => ({
          x: new Date(point.timestamp),
          y: point.bpm,
        })),
      });
    }

    if (sessionData && sessionData.hrMetrics?.data?.length && showSession) {
      result.push({
        id: 'sessionHeartRate',
        data: sessionData.hrMetrics.data.map(point => ({
          x: new Date(point.timestamp),
          y: point.value,
        })),
      });
    }

    return result;
  }, [heartRateData?.data, sessionData?.hrMetrics?.data, showSession]);

  const handleMarkerClick = useCallback(
    (sessionId: string) => {
      onMarkerClick?.(sessionId);
    },
    [onMarkerClick],
  );

  const sessionMarkers = useMemo(() => {
    const makeMarker = (timestamp: string, sessionId: string, sessionIndex: number) => ({
      axis: 'x' as const,
      value: new Date(timestamp),
      lineStyle: {
        stroke: getSessionColor(sessionIndex),
        strokeWidth: 2,
        strokeDasharray: '6 3',
      },
      onClick: () => handleMarkerClick(sessionId),
    });

    if (sessionData && !showSession) {
      const sessionIndex = allSessions.findIndex(s => s.id === sessionData.id);
      const idx = sessionIndex >= 0 ? sessionIndex : 0;
      return [
        makeMarker(sessionData.startTime, sessionData.id, idx),
        makeMarker(sessionData.endTime, sessionData.id, idx),
      ];
    }

    if (sessionData && showSession) {
      const idx = allSessions.findIndex(s => s.id === sessionData.id);
      return [
        makeMarker(sessionData.startTime, sessionData.id, idx >= 0 ? idx : 0),
        makeMarker(sessionData.endTime, sessionData.id, idx >= 0 ? idx : 0),
      ];
    }

    return allSessions.map((session, index) =>
      makeMarker(session.startTime, session.id, index),
    );
  }, [sessionData, allSessions, showSession, handleMarkerClick]);

  const xScaleConfig = useMemo(() => {
    const baseConfig = {
      type: 'time' as const,
      format: 'native' as const,
      useUTC: true,
      precision: 'minute' as const,
    };

    if (zoomDomain) {
      const start = new Date(zoomDomain.xStart);
      const end = new Date(zoomDomain.xEnd);

      if (start >= end) {
        const correctedStart = new Date(start.getTime() - 60 * 60 * 1000);
        return { ...baseConfig, min: correctedStart, max: start };
      }

      if (sessionData) {
        const sessionEndTime = new Date(sessionData.endTime);
        const actualEnd = new Date(
          Math.min(end.getTime(), sessionEndTime.getTime() + 10 * 60 * 1000),
        );
        return { ...baseConfig, min: start, max: actualEnd };
      }

      return { ...baseConfig, min: start, max: end };
    }

    if (heartRateData?.data?.length > 0) {
      const firstTimestamp = new Date(heartRateData.data[0].timestamp);
      const lastTimestamp = new Date(heartRateData.data[heartRateData.data.length - 1].timestamp);

      const minTime = new Date(firstTimestamp);
      minTime.setMinutes(minTime.getMinutes() - 30);

      const maxTime = new Date(lastTimestamp);
      maxTime.setMinutes(maxTime.getMinutes() + 30);

      return { ...baseConfig, min: minTime, max: maxTime };
    }

    return baseConfig;
  }, [zoomDomain, heartRateData?.data, sessionData]);

  const motionConfig = useMemo(() => {
    if (isZooming.current) {
      return {
        mass: 1,
        tension: 200,
        friction: 26,
        clamp: false,
      };
    }
    return {
      mass: 1,
      tension: 170,
      friction: 26,
      clamp: false,
    };
  }, [isZooming.current, zoomDomain]);

  const chartAnimationSettings = useMemo(
    () => ({
      animate: true,
      motionConfig: motionConfig,
    }),
    [motionConfig],
  );

  const legendData = useMemo(() => {
    const data = [
      {
        id: 'heartRate',
        label: 'Daily Heart Rate',
        color: colors.primaryColor,
      },
    ];

    if (sessionData && showSession) {
      data.push({
        id: 'sessionHeartRate',
        label: 'Session Heart Rate',
        color: colors.sessionColor,
      });
    }

    return data;
  }, [sessionData, showSession, colors.primaryColor, colors.sessionColor]);

  const yScaleConfig = useMemo(() => {
    let visibleValues: number[] = [];

    if (zoomDomain) {
      if (heartRateData?.data?.length > 0) {
        visibleValues.push(...filterDataByTimeRange(heartRateData.data, zoomDomain));
      }

      if (sessionData?.hrMetrics?.data?.length && showSession) {
        const hrPoints = sessionData.hrMetrics.data
          .filter(p => {
            const t = new Date(p.timestamp);
            return t >= zoomDomain.xStart && t <= zoomDomain.xEnd;
          })
          .map(p => p.value);
        visibleValues.push(...hrPoints);
      }

      if (visibleValues.length === 0 && sessionData?.hrMetrics && showSession) {
        visibleValues = [sessionData.hrMetrics.min, sessionData.hrMetrics.max];
      }
    } else {
      visibleValues = [heartRateData.min, heartRateData.max];
    }

    const yRange = calculateYRange(visibleValues);

    return {
      type: 'linear' as const,
      min: yRange.min,
      max: yRange.max,
      stacked: false,
      reverse: false,
    };
  }, [
    heartRateData.min,
    heartRateData.max,
    heartRateData.data,
    sessionData,
    showSession,
    zoomDomain,
  ]);

  const chartTheme = useMemo(() => chartThemeDark, []);

  const chartMargins = { top: 50, right: 40, bottom: 90, left: 70 };

  const chartProps = useMemo(
    () => ({
      data: transformedData,
      margin: chartMargins,
      xScale: xScaleConfig,
      xFormat: 'time:%H:%M',
      yScale: yScaleConfig,
      curve: 'monotoneX' as const,
      axisTop: null,
      axisRight: null,
      axisBottom: {
        format: '%H:%M',
        tickSize: 5,
        tickPadding: 5,
        tickRotation: -45,
        legend: 'Time of Day',
        legendOffset: 60,
        legendPosition: 'middle' as const,
        tickValues: 8,
      },
      axisLeft: {
        tickSize: 5,
        tickPadding: 5,
        tickRotation: 0,
        legend: 'Heart Rate (BPM)',
        legendOffset: -50,
        legendPosition: 'middle' as const,
      },
      colors: ({ id }: { id: string }) =>
        id === 'sessionHeartRate' ? colors.sessionColor : colors.primaryColor,
      lineWidth: 2.5,
      pointSize: 6,
      pointBorderWidth: 2,
      pointLabelYOffset: -12,
      enableGridX: false,
      enableArea: true,
      areaOpacity: 0.15,
      areaBaselineValue: yScaleConfig.min,
      enableSlices: 'x' as const,
      markers: sessionMarkers.map(marker => ({
        ...marker,
        axis: 'x' as const,
      })),
      legends: [
        {
          anchor: 'bottom-right' as const,
          direction: 'row' as const,
          justify: false,
          translateX: 0,
          translateY: 80,
          itemsSpacing: 30,
          itemDirection: 'left-to-right' as const,
          itemWidth: 120,
          itemHeight: 20,
          itemOpacity: 0.75,
          symbolSize: 12,
          symbolShape: 'circle' as const,
          symbolBorderColor: 'rgba(0, 0, 0, .5)',
          effects: [
            {
              on: 'hover' as const,
              style: {
                itemBackground: 'rgba(0, 0, 0, .03)',
                itemOpacity: 1,
              },
            },
          ],
          data: legendData,
        },
      ],
      sliceTooltip: ({ slice }: any) => (
        <div className="rounded-md border border-[var(--anl-border)] bg-[var(--anl-surface)] px-3 py-2 text-xs text-[var(--anl-text)] shadow-lg">
          {slice.points.map((point: any) => (
            <div key={point.id} className="flex items-center py-1">
              <div
                className="mr-2 h-4 w-4 rounded-full"
                style={{ backgroundColor: point.serieColor }}
              />
              <strong className="mr-2">
                {new Date(point.data.x as Date).toLocaleTimeString([], {
                  hour: '2-digit',
                  minute: '2-digit',
                })}
                :
              </strong>
              {Number(point.data.y)} BPM
              {point.serieId === 'sessionHeartRate' && ' (Session)'}
            </div>
          ))}
        </div>
      ),
      theme: chartTheme,
    }),
    [
      transformedData,
      chartMargins,
      xScaleConfig,
      yScaleConfig,
      colors.sessionColor,
      colors.primaryColor,
      sessionMarkers,
      legendData,
      chartTheme,
    ],
  );

  const exportData = useMemo(() => {
    if (!heartRateData?.data?.length) return null;

    const baseData = heartRateData.data.map(point => ({
      timestamp: new Date(point.timestamp).toISOString(),
      heartRate_bpm: point.bpm,
    }));

    if (sessionData?.hrMetrics?.data?.length && showSession) {
      const sessionExportData = sessionData.hrMetrics.data.map(point => ({
        timestamp: new Date(point.timestamp).toISOString(),
        heartRate_bpm: point.value,
        sessionId: sessionData.id,
        sessionName: sessionData.name,
      }));
      return [...baseData, ...sessionExportData];
    }

    return baseData;
  }, [heartRateData, sessionData, showSession]);

  const timeRange = useMemo(() => {
    if (!heartRateData?.data?.length) return null;

    const sortedData = [...heartRateData.data].sort(
      (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
    );

    return {
      start: new Date(sortedData[0].timestamp),
      end: new Date(sortedData[sortedData.length - 1].timestamp),
    };
  }, [heartRateData?.data]);

  const showZoomInstruction = !isBrushDisabled && !zoomDomain;

  return (
    <div className="h-[500px] overflow-hidden" key={chartDataKey}>
      <div className="mb-2 flex items-center justify-between">
        <h4 className="text-sm font-medium text-gray-700">Heart Rate Chart</h4>
        {exportData && (
          <ExportButton
            data={exportData}
            config={{
              fileName: `heart_rate_data${sessionData ? `_${sessionData.name.replace(/\s+/g, '_')}` : ''}`,
              date: true,
              preserveTimestamp: true,
            }}
            format={ExportFormat.BOTH}
            className="hover:bg-gray-100"
          />
        )}
      </div>

      {showZoomInstruction && (
        <div className="mb-2 text-xs text-[var(--anl-accent)]">
          Click and drag to zoom to selection
        </div>
      )}

      <ZoomBrush
        disabled={!!isBrushDisabled}
        timeRange={timeRange}
        zoomDomain={zoomDomain}
        onZoomChange={onZoomChange}
        className="h-[calc(100%-60px)]"
        config={{
          marginLeft: chartMargins.left,
          marginRight: chartMargins.right,
        }}
      >
        <ResponsiveLine {...chartProps} {...chartAnimationSettings} />
      </ZoomBrush>
    </div>
  );
};
